import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { apiFetch } from '../lib/api'
import type { Admin } from '../lib/types'
import { useAdmin } from '../lib/session'
import { PageHeader, formatDate } from '../components/page'
import { ErrorState, Spinner } from '../components/states'
import { Badge } from '../components/badge'

export function AdminsPage() {
	const queryClient = useQueryClient()
	const { data: me } = useAdmin()
	const [creating, setCreating] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const { data, isPending, error: loadError, refetch } = useQuery({
		queryKey: ['admins'],
		queryFn: () => apiFetch<{ admins: Admin[] }>('/api/admin/admins').then((r) => r.admins),
	})

	const toggle = useMutation({
		mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
			apiFetch(`/api/admin/admins/${id}`, { method: 'PATCH', body: { isActive } }),
		onSuccess: () => {
			setError(null)
			queryClient.invalidateQueries({ queryKey: ['admins'] })
		},
		onError: (e) => setError((e as Error).message),
	})

	return (
		<>
			<PageHeader
				title="Admins"
				subtitle="Everyone with access. There is one level of access — every active admin can do everything here."
				actions={
					<button type="button" className="btn-primary" onClick={() => setCreating(true)}>
						<Plus className="h-4 w-4" /> Add admin
					</button>
				}
			/>

			{error && <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

			<div className="card">
				{isPending ? (
					<Spinner />
				) : loadError ? (
					<ErrorState error={loadError} onRetry={refetch} />
				) : (
					<ul className="divide-y divide-line">
						{data.map((admin) => {
							const isSelf = admin.id === me?.adminId
							return (
								<li key={admin.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
									<div className="min-w-0">
										<div className="flex items-center gap-2">
											<span className="truncate font-medium">{admin.email}</span>
											{isSelf && <Badge tone="info">you</Badge>}
											{!admin.isActive && <Badge tone="danger">deactivated</Badge>}
										</div>
										<div className="mono truncate">
											{admin.name} · last signed in {formatDate(admin.lastLoginAt)}
										</div>
									</div>
									<button
										type="button"
										className={admin.isActive ? 'btn-danger' : 'btn-secondary'}
										// Deactivating yourself would 403 your own session with no way
										// back through the UI, so the backend refuses it too.
										disabled={isSelf || toggle.isPending}
										title={isSelf ? 'You cannot change your own status' : undefined}
										onClick={() => toggle.mutate({ id: admin.id, isActive: !admin.isActive })}
									>
										{admin.isActive ? 'Deactivate' : 'Reactivate'}
									</button>
								</li>
							)
						})}
					</ul>
				)}
			</div>

			{creating && (
				<CreateAdminDialog
					onClose={() => setCreating(false)}
					onCreated={() => {
						setCreating(false)
						queryClient.invalidateQueries({ queryKey: ['admins'] })
					}}
				/>
			)}
		</>
	)
}

// No creation secret here: the server supplies it, because this session already
// proves the authority. Only the standalone /bootstrap page ever asks for it.
function CreateAdminDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
	const [form, setForm] = useState({ name: '', email: '', password: '' })

	const create = useMutation({
		mutationFn: () => apiFetch('/api/admin/admins', { method: 'POST', body: form }),
		onSuccess: onCreated,
	})

	function set(key: keyof typeof form) {
		return (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }))
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<button type="button" aria-hidden tabIndex={-1} className="absolute inset-0 bg-black/40" onClick={onClose} />
			<form
				role="dialog"
				aria-modal
				className="card relative z-10 w-full max-w-sm p-5"
				onSubmit={(e) => {
					e.preventDefault()
					create.mutate()
				}}
			>
				<h2 className="mb-4 font-display text-lg font-semibold">Add admin</h2>

				<label className="label" htmlFor="an">Name</label>
				<input id="an" className="input mb-3" value={form.name} onChange={set('name')} required />

				<label className="label" htmlFor="ae">Email</label>
				<input id="ae" type="email" className="input mb-3" value={form.email} onChange={set('email')} required />

				<label className="label" htmlFor="ap">Password</label>
				<input id="ap" type="password" className="input mb-1" value={form.password} onChange={set('password')} minLength={12} autoComplete="new-password" required />
				<p className="mb-3 text-xs text-ink-faint">At least 12 characters. Share it with them securely.</p>

				{create.error && (
					<p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
						{(create.error as Error).message}
					</p>
				)}

				<div className="flex justify-end gap-2">
					<button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
					<button type="submit" className="btn-primary" disabled={create.isPending}>
						{create.isPending ? 'Creating…' : 'Create'}
					</button>
				</div>
			</form>
		</div>
	)
}
